import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { get, post } from "util/requests";
import colors from "colors";
import moment from "moment";
import styles from "./consent_view.module.css";
import LoadingButton from "@mui/lab/LoadingButton";
import DomainTag from "views/subjects/components/domain_tag";
import { useLocation } from "react-router-dom";
import ConsentUpload from "./components/consent_upload";
import { CheckIcon } from "ui/icons";
import { ThemeProvider } from "@mui/material";
import { theme } from "config";
import { renderFlagsRow } from "views/edc/components/crf/log_form_table";
import router from "routes";

const render_type = (type) => {
  return (
    {
      deferred_waiver_consent: "Deferred",
      surrogate: "Surrogate",
      regained_capacity: "Regained Capacity",
      "2_physician_emerg": "2 Physician Emergency",
      patient: "Patient",
    }[type] || type
  );
};

export function Table(props) {
  const { crf_id, subject_id, domain_id, updateCrfStatus, form_type, multi_entry_crf_id, data, onRowClick } = props;
  const permissions = useSelector((state) => state.user.userData.permissions);

  const columns = [
    {
      field: "consent_type",
      headerName: "Type",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => render_type(row.consent_type),
    },
    {
      field: "flags",
      headerName: "Flags",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => renderFlagsRow(row, row.query, permissions),
    },
    {
      field: "consent_date",
      headerName: "Consent Date",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        if (row.consent_date && row.consent_time) {
          return `${row.consent_date} ${row.consent_time}`;
        } else {
          return moment(row.created_on).format("yyyy-MM-DD HH:mm");
        }
      },
    },
    {
      field: "domains",
      headerName: "Domains",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <React.Fragment>
          {(row.domains && (
            <div className={styles.domain_tile_wrapper}>
              {row.domains.map((d) => (
                <DomainTag text={d} />
              ))}
            </div>
          )) || <div />}
        </React.Fragment>
      ),
    },
    {
      field: "files",
      headerName: "Uploaded File",
      align: "center",
      headerAlign: "center",
      width: 150,
      renderCell: ({ row }) => (row.file_id ? <CheckIcon /> : null),
    },
    {
      field: "external_file_ref",
      headerName: "External Reference",
      align: "center",
      headerAlign: "center",
      width: 200,
    },
    {
      field: "created_on",
      headerName: "Created On",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (row.created_on && moment(row.created_on).format("yyyy-MMM-DD HH:mm")) || "",
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <DataGrid
        rows={data}
        columns={columns}
        disableColumnSelector
        disableRowSelectionOnClick
        disableColumnMenu
        pageSize={10}
        rowsPerPageOptions={[10]}
        hideFooterSelectedRowCount
        pageSizeOptions={[10]}
        columnHeaderHeight={34}
        rowHeight={34}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        sx={{
          boxShadow: 0,
          border: 1,
          borderColor: "#f0f0f0",
          "& .MuiDataGrid-cell:hover": {
            color: "#8349fd",
            cursor: "pointer",
          },
          "& .MuiDataGrid-topContainer .MuiDataGrid-columnHeader ": {
            // backgroundColor: "#f7f3fe",
            backgroundColor: colors.table_header_background,
          },
          "& .MuiDataGrid-topContainer .MuiDataGrid-filler ": {
            // backgroundColor: "#f7f3fe",
            backgroundColor: colors.table_header_background,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            color: "#333",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none", // Remove the focus outline
          },
        }}
        components={{
          NoRowsOverlay: () => <div className={styles.no_rows_overlay}>No Consent Records</div>,
        }}
        onRowClick={(data) => {
          onRowClick(data.row);
        }}
      />
    </ThemeProvider>
  );
}

export default function ConsentView(props) {
  const { crf_id, subject_id, domain_id, study_id } = props;
  const permissions = useSelector((state) => state.user.userData.permissions);

  const [data, setData] = useState([]);
  const location = useLocation();
  const [showingModal, setShowingModal] = useState(false);

  useEffect(() => {
    post("/footprint", {
      path: location.pathname,
      action: "pageload",
      name: "consent_table",
      subject_id,
    });
  }, [location.pathname]);

  const fetch_table_data = () => {
    get(`/consent_table_view/${subject_id}`).then((ret) => {
      if (["no_domain_access", "no_site_access"].includes(ret.data.status)) {
        router.navigate("/subjects");
        return;
      }
      setData(ret.data);
    });
  };

  useEffect(() => {
    fetch_table_data();
  }, [crf_id, subject_id]);

  return (
    <div className={styles.wrapper}>
      <div>
        <div className={styles.stretch_header}>
          <div>
            <strong>Consent</strong>
          </div>
          {permissions.includes("consent_record_update") ? (
            <div>
              <LoadingButton
                variant="contained"
                loading={false}
                onClick={() => setShowingModal(true)}
                sx={{
                  fontSize: 14,
                  padding: "2px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                }}
              >
                ADD CONSENT
              </LoadingButton>
            </div>
          ) : null}
        </div>
      </div>

      <Table {...props} data={data?.rows || []} onRowClick={(data) => setShowingModal(data)} />
      <ConsentUpload
        subject_id={subject_id}
        showingModal={showingModal}
        setShowingModal={setShowingModal}
        RefetchTableData={fetch_table_data}
        study_id={study_id}
        is_consent_upload={data?.is_consent_upload}
      />
    </div>
  );
}
